import React, {useMemo, useState} from "react";
import 'react-calendar-timeline/lib/Timeline.css';
import {Box, LinearProgress, } from "@mui/material";
import {Outlet} from "react-router-dom";

import usePerformerShifts from "../hooks/usePerformerShifts";
import usePerformerData from "../hooks/usePerformerData";
import useBuffer from "../hooks/useBuffer";
import useDataReloadForSchedule from "../hooks/useDataReloadForSchedule";
import {useTimezone} from "../hooks/useTimezone";
import useOpenUrlInNewTab from "../hooks/useOpenUrlInNewTab";

import TimeslotAndSessionsTimeline from "../components/TimeslotAndSessionsTimeline";
import Loader from "../components/Loader";
import buildSortingDefinitions from "../helpers/ScheduleUtils";
import SchedulePageHeader from "../components/SchedulePageHeader";
import {buildMemberLookupUrl} from "./ParentInfoLookupView";

export default function ViewSchedule() {
    const {timezone} = useTimezone();
    const openUrlInNewTab = useOpenUrlInNewTab();
    const {
        handleManualRefresh,
        handleTimelineBoundsChanged,
        handleTimelineTimeChanged,
        setAreShiftsFiltered,
        areShiftsFiltered,
        isShiftDataLoading,
        shiftData,
        currentViewType,
        lastVisibleBounds,
        serviceTypeOptions,
        selectedServiceTypeKeycodes,
        setSelectedServiceTypeKeycodes
    } = usePerformerShifts();
    const {isPerformerDataLoading, performerDataLoadingError, performerData} = usePerformerData();
    const sortingDefinitions = buildSortingDefinitions(shiftData, lastVisibleBounds);
    const [sortOption, handleSortOptionUpdate] = useState(Object.keys(sortingDefinitions).find(key => sortingDefinitions[key].default));
    const {bufferSize} = useBuffer(performerData, timezone);

    useDataReloadForSchedule(handleTimelineBoundsChanged);

    const sortedPerformerData = useMemo(() => {
        return [...performerData].sort(sortingDefinitions[sortOption].sortingFunction);
    }, [performerData, sortOption, lastVisibleBounds, currentViewType, sortingDefinitions]);

    if (isPerformerDataLoading || !performerData) {
        return <Loader/>;
    }

    if (performerDataLoadingError) {
        return `Request Error! ${performerDataLoadingError.message}`;
    }

    function lookupMember(memberId) {
        const url = buildMemberLookupUrl(memberId);
        openUrlInNewTab(url);
    }

    return (
        <Box id="performer-calendar">
            <SchedulePageHeader
                sortOption={sortOption}
                handleSortOptionUpdate={handleSortOptionUpdate}
                sortingDefinitions={sortingDefinitions}
                handleManualRefresh={handleManualRefresh}
                setAreShiftsFiltered={setAreShiftsFiltered}
                areShiftsFiltered={areShiftsFiltered}
                loading={isShiftDataLoading}
                availableServiceTypes={serviceTypeOptions}
                selectedServiceTypeKeycodes={selectedServiceTypeKeycodes}
                setSelectedServiceTypeKeycodes={setSelectedServiceTypeKeycodes}/>

            <Outlet/>

            {isShiftDataLoading && <LinearProgress/>}
            {!isShiftDataLoading && <Box sx={{my: 0.5}}></Box>}

            <TimeslotAndSessionsTimeline
                performerData={sortedPerformerData}
                shiftData={shiftData}
                currentViewType={currentViewType}
                bufferSize={bufferSize}
                onChildDetailsDrilledInto={lookupMember}
                onTimelineBoundsChanged={handleTimelineBoundsChanged}
                onTimelineTimeChanged={handleTimelineTimeChanged}
                currentSortingDefinition={sortingDefinitions?.[sortOption]} />
        </Box>
    );
}